import {FetchBaseQueryError} from '@reduxjs/toolkit/query';

import {omneticApi} from '@dms/api/base';

import {assignSafely, getByPathSafely} from 'shared/web';

import {CreateUserVehicleSummaryRequestBody} from '../sourcing/types';
import {List, OmneticMutationLifecycleApi} from '../sourcing/utils/sourcingApiHelpers';
import {
  AuctionServerResponseBody,
  AuctionVehicleDetailResponseBody,
  CreateAuctionUserSeenApiArg,
  CreateAuctionUserSeenApiResponse,
  GetAuctionUserComparisonApiArg,
  GetAuctionUserComparisonApiResponse,
  GetAuctionUserFavouriteApiArg,
  GetAuctionUserFavouriteApiResponse,
  GetAuctionUserHiddenApiArg,
  GetAuctionUserHiddenApiResponse,
  GetAuctionUserToBuyApiArg,
  GetAuctionUserToBuyApiResponse,
  GetAuctionVehicleCountApiArg,
  GetAuctionVehicleCountApiResponse,
  GetAuctionVehicleDetailApiArg,
  GetAuctionVehicleDetailApiResponse,
  GetAuctionVehicleListApiArg,
  GetAuctionVehicleListApiResponse,
  SortAuctionUserComparisonRequestApiArg,
  SortAuctionUserComparisonRequestBody,
} from './types';
import {filterOutAuctionDuplicates} from './utils/filterOutAuctionDuplicates';
import {normalizeAuctionCurrencies} from './utils/normalizeAuctionCurrencies';

type AuctionBulkUserActionApiArg = {
  body: {externalIds: string[]};
};

const ROWS_PER_PAGE = 20;
const AUCTION_LIST_QUERY_NAMES = [
  'getSourcingAuctionList',
  'getAuctionUserFavourite',
  'getAuctionUserComparison',
  'getAuctionUserHidden',
  'getAuctionUserToBuy',
] as const;

type AuctionListEndpointName = (typeof AUCTION_LIST_QUERY_NAMES)[number];

const getOffset = (page: number = 1) => (page - 1) * ROWS_PER_PAGE;

function isAuctionListEndpointName(endpointName: string): endpointName is AuctionListEndpointName {
  return AUCTION_LIST_QUERY_NAMES.includes(endpointName as AuctionListEndpointName);
}

const sourcingAuctionApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getSourcingAuctionList: build.query<
      GetAuctionVehicleListApiResponse,
      GetAuctionVehicleListApiArg
    >({
      queryFn: async (queryArg, queryApi, _extraOptions, fetchWithBaseQuery) => {
        const {page, body} = queryArg;
        const initialOffset = getOffset(page);
        const vehicles: AuctionVehicleDetailResponseBody[] = [];
        let hasMore = true;
        let offset = initialOffset;
        let size = 0;

        try {
          while (hasMore) {
            const result = await fetchWithBaseQuery({
              url: '/dms/v1/auction-vehicle',
              method: 'POST',
              body: body ?? {},
              params: {
                offset,
                size: ROWS_PER_PAGE,
                currency: queryArg.currency,
                'orderBy[]': queryArg.orderBy,
              },
            });

            if (result.error) {
              return {error: result.error as FetchBaseQueryError};
            }

            const rawData = result.data as GetAuctionVehicleListApiResponse;
            const fetchedData = {
              ...rawData,
              data: rawData.data.map(normalizeAuctionCurrencies),
            };
            if (!queryArg.isPolling) {
              return {data: fetchedData};
            }

            // Polling path: loop to discover all new vehicles not yet in the infinite scroll cache
            const state = queryApi.getState() as Record<string, unknown>;
            const serializedKey = `getSourcingAuctionList_${queryArg.currency}_${queryArg.orderBy}_infinite_${JSON.stringify(queryArg.body)}`;
            const cachedQueryState = getByPathSafely(state, [
              'rtk_omneticApi',
              'queries',
              serializedKey,
            ]) as {data?: GetAuctionVehicleListApiResponse} | undefined;
            const cachedVehicles = cachedQueryState?.data?.data ?? [];
            vehicles.push(...fetchedData.data);
            const newVehicles = filterOutAuctionDuplicates(cachedVehicles, fetchedData.data);
            hasMore = cachedVehicles.length > 0 && newVehicles.length === ROWS_PER_PAGE;
            offset += ROWS_PER_PAGE;
            size += fetchedData.links.size;
          }

          return {data: {data: vehicles, links: {size, offset: initialOffset}}};
        } catch (error) {
          return {error: error as FetchBaseQueryError};
        }
      },
      serializeQueryArgs: ({endpointName, queryArgs}) => {
        const type = queryArgs.isPolling ? 'polling' : 'infinite';
        return `${endpointName}_${queryArgs.currency}_${queryArgs.orderBy}_${type}_${JSON.stringify(queryArgs.body)}`;
      },
      merge: (currentCache, newData, {arg}) => {
        const newVehicles = filterOutAuctionDuplicates(currentCache.data, newData.data);

        if (arg.isPolling) {
          currentCache.data.unshift(...newVehicles);
        } else {
          currentCache.data.push(...newVehicles);
        }

        if (currentCache.links) {
          currentCache.links.size = currentCache.data.length;
        }
      },
      forceRefetch: (params) => {
        const isPolling = params.currentArg?.isPolling;
        const currentPage = params.currentArg?.page ?? 1;
        const previousPage = params.previousArg?.page;

        if (isPolling) {
          return true;
        }

        return currentPage === (previousPage ?? 1) + 1;
      },
      onQueryStarted: async (arg, api) => {
        try {
          await api.queryFulfilled;
          const currentCache = api.getCacheEntry();
          const currentVehicles = currentCache?.data?.data;
          const oppositeArgs = {...arg, isPolling: arg.isPolling !== true};

          if (!currentVehicles) {
            return;
          }

          api.dispatch(
            sourcingAuctionApi.util.updateQueryData(
              'getSourcingAuctionList',
              oppositeArgs,
              (draft) => {
                const newVehicles = filterOutAuctionDuplicates(draft.data, currentVehicles);

                if (newVehicles.length === 0) {
                  return;
                }

                if (arg.isPolling === true) {
                  draft.data.unshift(...newVehicles);
                } else {
                  draft.data.push(...newVehicles);
                }

                if (draft.links) {
                  draft.links.size = draft.data.length;
                }
              }
            )
          );
        } catch {
          // Silently fail cache syncing - it's not critical for functionality
        }
      },
      providesTags: ['SourcingAuctionList', 'SourcingAuctionAllListsWithSameResponseStructure'],
      keepUnusedDataFor: 1800,
    }),

    getSourcingAuctionDetail: build.query<
      GetAuctionVehicleDetailApiResponse,
      GetAuctionVehicleDetailApiArg
    >({
      query: (arg) => ({
        url: `/dms/v1/auction-vehicle/${arg.externalId}`,
        method: 'GET',
        params: {
          margin: arg.margin,
          currency: arg.currency,
          buyerCountry: arg.buyerCountry,
        },
      }),
      transformResponse: (response: GetAuctionVehicleDetailApiResponse) =>
        normalizeAuctionCurrencies(response),
      providesTags: (_result, _error, arg) => [{type: 'SourcingAuctionDetail', id: arg.externalId}],
    }),

    getSourcingAuctionCount: build.query<
      GetAuctionVehicleCountApiResponse,
      GetAuctionVehicleCountApiArg
    >({
      query: (arg) => ({
        url: '/dms/v1/auction-vehicle/_count',
        method: 'POST',
        body: arg.body ?? {},
        params: {
          currency: arg.currency,
        },
      }),
      providesTags: ['SourcingAuctionList'],
    }),

    getAuctionUserFavourite: build.query<
      GetAuctionUserFavouriteApiResponse,
      GetAuctionUserFavouriteApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle/user-favourite',
        method: 'GET',
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (response: GetAuctionUserFavouriteApiResponse) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      providesTags: ['SourcingAuctionList', 'SourcingAuctionAllListsWithSameResponseStructure'],
    }),

    getAuctionUserComparison: build.query<
      GetAuctionUserComparisonApiResponse,
      GetAuctionUserComparisonApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle/user-comparison',
        method: 'GET',
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (response: GetAuctionUserComparisonApiResponse) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      providesTags: ['SourcingAuctionList', 'SourcingAuctionAllListsWithSameResponseStructure'],
    }),

    getAuctionUserHidden: build.query<GetAuctionUserHiddenApiResponse, GetAuctionUserHiddenApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle/user-hidden',
        method: 'GET',
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (response: GetAuctionUserHiddenApiResponse) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      providesTags: ['SourcingAuctionList', 'SourcingAuctionAllListsWithSameResponseStructure'],
    }),

    getAuctionUserToBuy: build.query<GetAuctionUserToBuyApiResponse, GetAuctionUserToBuyApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/auction-vehicle/user-to-buy',
        method: 'GET',
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          'orderBy[]': queryArg.orderBy,
        },
      }),
      transformResponse: (response: GetAuctionUserToBuyApiResponse) => ({
        ...response,
        data: response.data.map(normalizeAuctionCurrencies),
      }),
      providesTags: ['SourcingAuctionList', 'SourcingAuctionAllListsWithSameResponseStructure'],
    }),

    auctionBulkDeleteUserFavourite: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-favourite/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getAuctionUserFavourite');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', false);
      },
      invalidatesTags: ['User', 'SourcingAuctionList', 'SourcingAuctionDetail'],
    }),

    auctionBulkCreateUserFavourite: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-favourite/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', true);
      },
      invalidatesTags: ['User', 'SourcingAuctionList', 'SourcingAuctionDetail'],
    }),

    auctionBulkDeleteUserComparison: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-comparison/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getAuctionUserComparison');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', false);
      },
      invalidatesTags: ['User', 'SourcingAuctionList', 'SourcingAuctionDetail'],
    }),

    auctionBulkCreateUserComparison: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-comparison/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', true);
      },
      invalidatesTags: ['User', 'SourcingAuctionList', 'SourcingAuctionDetail'],
    }),

    auctionReorderUserComparison: build.mutation<void, SortAuctionUserComparisonRequestApiArg>({
      query: (queryArg) => {
        const requestBody: SortAuctionUserComparisonRequestBody = queryArg.map(
          (vehicle, index) => ({
            externalId: vehicle.auctionVehicle.adId ?? '',
            position: index + 1,
          })
        );

        return {
          url: '/dms/v1/auction-vehicle/user-comparison/sort',
          method: 'POST',
          body: {
            auctionVehiclesWithPositions: requestBody,
          },
        };
      },
      invalidatesTags: ['SourcingAuctionList', 'SourcingAuctionDetail'],
    }),

    auctionBulkDeleteUserHidden: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-hidden/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getAuctionUserHidden');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', false);
      },
      invalidatesTags: ['User', 'SourcingAuctionList', 'SourcingAuctionDetail'],
    }),

    auctionBulkCreateUserHidden: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-hidden/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getSourcingAuctionList');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', true);
      },
      invalidatesTags: ['User', 'SourcingAuctionList', 'SourcingAuctionDetail'],
    }),

    auctionBulkDeleteUserToBuy: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-to-buy/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'getAuctionUserToBuy');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', false);
      },
      invalidatesTags: ['User', 'SourcingAuctionList', 'SourcingAuctionDetail'],
    }),

    auctionBulkCreateUserToBuy: build.mutation<void, AuctionBulkUserActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-to-buy/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', true);
      },
      invalidatesTags: ['User', 'SourcingAuctionList', 'SourcingAuctionDetail'],
    }),

    sourcingSetAuctionUserSeen: build.mutation<
      CreateAuctionUserSeenApiResponse,
      CreateAuctionUserSeenApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/auction-vehicle/user-seen`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
      },
    }),

    getAuctionServerList: build.query<AuctionServerResponseBody[], void>({
      query: () => ({url: '/dms/v1/sourcing/auction-server'}),
    }),
  }),
});

function optimisticUpdateVehicleDetailCache<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (queryArgs.body.externalIds.includes(originalArgs.externalId)) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            assignSafely(draft, list, isInList);
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}

function optimisticUpdateVehicleListCaches<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (!isAuctionListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            if (queryArgs.body.externalIds.includes(vehicle.auctionVehicle.adId ?? '')) {
              assignSafely(vehicle, list, isInList);
            }
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

function optimisticRemoveVehiclesFromList<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  listToRemoveEndpointName?: AuctionListEndpointName
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (!isAuctionListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (listToRemoveEndpointName === undefined || endpointName === listToRemoveEndpointName) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            draft.data = draft.data.filter(
              (vehicle) => !queryArgs.body.externalIds.includes(vehicle.auctionVehicle.adId ?? '')
            );
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}

export function updateAuctionVehicleSummaryInCaches(
  sourcingVehicleId: string,
  body: CreateUserVehicleSummaryRequestBody,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAuctionAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (!isAuctionListEndpointName(endpointName)) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            if (vehicle.auctionVehicle.adId === sourcingVehicleId) {
              vehicle.vehicleSummary = {
                id: vehicle.vehicleSummary?.id ?? '',
                purchasePrice: body.purchasePrice ?? null,
                sellingPrice: body.sellingPrice ?? null,
                comment: body.comment ?? null,
              };
            }
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });

  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        return;
      }

      if (originalArgs.externalId !== sourcingVehicleId) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.vehicleSummary = {
            id: draft.vehicleSummary?.id ?? '',
            purchasePrice: body.purchasePrice ?? null,
            sellingPrice: body.sellingPrice ?? null,
            comment: body.comment ?? null,
          };
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

export function updateAuctionDetailCacheByExternalId(
  externalId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>,
  updateFn: (draft: AuctionVehicleDetailResponseBody) => void
) {
  sourcingAuctionApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingAuctionDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'getSourcingAuctionDetail') {
        return;
      }

      if (originalArgs.externalId !== externalId) {
        return;
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingAuctionApi.util.updateQueryData(endpointName, originalArgs, updateFn)
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

export const {
  useGetSourcingAuctionListQuery,
  useGetSourcingAuctionDetailQuery,
  useGetSourcingAuctionCountQuery,
  useGetAuctionUserFavouriteQuery,
  useGetAuctionUserComparisonQuery,
  useGetAuctionUserHiddenQuery,
  useGetAuctionUserToBuyQuery,
  useAuctionBulkDeleteUserFavouriteMutation,
  useAuctionBulkCreateUserFavouriteMutation,
  useAuctionBulkDeleteUserComparisonMutation,
  useAuctionBulkCreateUserComparisonMutation,
  useAuctionReorderUserComparisonMutation,
  useAuctionBulkDeleteUserHiddenMutation,
  useAuctionBulkCreateUserHiddenMutation,
  useAuctionBulkDeleteUserToBuyMutation,
  useAuctionBulkCreateUserToBuyMutation,
  useSourcingSetAuctionUserSeenMutation,
  useGetAuctionServerListQuery,
} = sourcingAuctionApi;
